'use client';

import type { LCColorValue } from '@unifyapps/defs/types/styleObject';
import type { CSSProperties, Ref } from 'react';
import { useResolvedColor } from '../../../../../components/DrawCanvas/hooks';
import { HIGHLIGHT_PADDING, PATH_STYLE, SVG_STYLE } from '../constants';
import { resolveOverlayOpacity } from '../utils/resolveOverlayOpacity';

interface HighlightOverlayProps {
  svgRef: Ref<SVGSVGElement>;
  pathRef: Ref<SVGPathElement>;
  overlayColor?: string | LCColorValue;
  overlayOpacity?: string;
  enableInteractionWithPage?: boolean;
  /** When false, a transparent rect covers the hole area to block events from reaching the target.
   *  This prevents parent hover effects (e.g. navbar item wrappers) without touching the target DOM. */
  enableInteractionWithTargetElement?: boolean;
  /** Position/size of the highlight hole, from useHighlightOverlay stage. */
  targetBounds?: { x: number; y: number; width: number; height: number } | null;
}

/**
 * HighlightOverlay - A reusable component that renders an SVG overlay
 * with a semi-transparent dark backdrop and a "hole" for highlighting elements.
 * This component is used by both DesktopWalkthroughGuide and BuilderStepOverlay.
 */
export function HighlightOverlay({
  svgRef,
  pathRef,
  overlayColor,
  overlayOpacity,
  enableInteractionWithPage = true,
  enableInteractionWithTargetElement = true,
  targetBounds,
}: HighlightOverlayProps) {
  const resolvedColor = useResolvedColor(overlayColor, PATH_STYLE.fill as string);

  const resolvedOpacity = resolveOverlayOpacity(overlayOpacity, PATH_STYLE.opacity as number);

  const pathStyle: CSSProperties = {
    ...PATH_STYLE,
    fill: resolvedColor,
    opacity: resolvedOpacity,
    // Use 'visiblePainted' so pointer events only apply to the filled (painted) area,
    // allowing clicks to pass through the transparent hole to the target element
    pointerEvents: enableInteractionWithPage ? 'none' : 'visiblePainted',
  };

  const svgStyle: CSSProperties = {
    ...SVG_STYLE,
    // SVG element should always have pointer-events: none so clicks can pass through
    // to elements below. The path handles capturing clicks on the backdrop area.
    pointerEvents: 'none',
  };

  // When target interaction is disabled, render a transparent rect that covers the hole area.
  // This blocks pointer events at the overlay level — events never reach the iframe/page,
  // so parent elements (e.g. navbar wrappers with hover styles) don't receive hover.
  const showBlockingRect = !enableInteractionWithTargetElement && targetBounds;

  return (
    <svg ref={svgRef} style={svgStyle}>
      <path ref={pathRef} style={pathStyle} />
      {showBlockingRect ? (
        <rect
          fill="transparent"
          height={targetBounds.height + HIGHLIGHT_PADDING * 2}
          style={{ pointerEvents: 'all', cursor: 'default' }}
          width={targetBounds.width + HIGHLIGHT_PADDING * 2}
          x={targetBounds.x - HIGHLIGHT_PADDING}
          y={targetBounds.y - HIGHLIGHT_PADDING}
        />
      ) : null}
    </svg>
  );
}
